
import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 1
const TOAST_DEFAULT_VISIBLE_DURATION = 3000
const TOAST_MAX_VISIBLE_DURATION = 8000
const TOAST_REMOVE_DELAY = 1000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: string
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: string
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
const toastVisibleTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const clearVisibleTimeout = (toastId: string) => {
  const timeout = toastVisibleTimeouts.get(toastId)
  if (!timeout) return

  clearTimeout(timeout)
  toastVisibleTimeouts.delete(toastId)
}

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId) {
        clearVisibleTimeout(toastId)
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          clearVisibleTimeout(toast.id)
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }

    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        toastVisibleTimeouts.forEach((timeout) => clearTimeout(timeout))
        toastVisibleTimeouts.clear()
        return {
          ...state,
          toasts: [],
        }
      }
      clearVisibleTimeout(action.toastId)
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function clampToastDuration(duration: Toast["duration"]): number {
  if (typeof duration !== "number" || !Number.isFinite(duration)) {
    return TOAST_DEFAULT_VISIBLE_DURATION
  }

  return Math.min(duration, TOAST_MAX_VISIBLE_DURATION)
}

function scheduleAutoDismiss(toastId: string, duration: number) {
  clearVisibleTimeout(toastId)

  const timeout = setTimeout(() => {
    toastVisibleTimeouts.delete(toastId)
    dispatch({ type: "DISMISS_TOAST", toastId })
  }, duration)

  toastVisibleTimeouts.set(toastId, timeout)
}

function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) => {
    const duration = clampToastDuration(props.duration)
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id, duration },
    })
    scheduleAutoDismiss(id, duration)
  }
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })
  const duration = clampToastDuration(props.duration)

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      duration,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })
  scheduleAutoDismiss(id, duration)

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
